/* ==========================================================================
   CB BLOG ARTICLE — auto table of contents
   Collects every <h1>-<h6> inside the article body, assigns stable anchor IDs,
   and builds an "In this article" jump-link list.

   Where it renders:
   - Desktop (>1024px), when the section provides a rail
     ([data-cb-toc-rail] inside [data-cb-article-layout]): a sticky left rail
     beside the body, with the current section highlighted while scrolling.
   - Tablet/mobile, or a section without a rail: a card at the top of the body.
   Both copies are built up front; CSS decides which one is visible.
   Re-runs on shopify:section:load so the TOC also shows in the theme editor.
   Docs: docs/CB.BLOG_ARTICLE.MD
   ========================================================================== */
(function () {
  var MIN_HEADINGS = 2; // not worth a TOC for a single heading

  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  }

  function buildNav(headings, modifier) {
    var nav = document.createElement('nav');
    nav.className = 'cb-article-toc cb-article-toc--' + modifier;
    nav.setAttribute('aria-label', 'Table of contents');

    var title = document.createElement('span');
    title.className = 'cb-article-toc__title';
    title.textContent = 'In this article';
    nav.appendChild(title);

    // Top level = the highest heading level used (usually h2). Deeper headings
    // are "sub" items, tagged with the top-level heading they sit under so the
    // rail can collapse them to the section being read.
    var topLevel = 6;
    headings.forEach(function (h) {
      topLevel = Math.min(topLevel, parseInt(h.tagName.charAt(1), 10));
    });
    var parentId = '';

    var ul = document.createElement('ul');
    headings.forEach(function (h) {
      var li = document.createElement('li');
      li.className = 'cb-article-toc__item cb-article-toc__item--' + h.tagName.toLowerCase();
      if (parseInt(h.tagName.charAt(1), 10) === topLevel) {
        parentId = h.id;
      } else if (parentId) {
        li.className += ' cb-article-toc__item--sub';
        li.setAttribute('data-cb-toc-parent', parentId);
      }
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.setAttribute('data-cb-toc-target', h.id);
      a.textContent = (h.textContent || '').trim();
      li.appendChild(a);
      ul.appendChild(li);
    });
    nav.appendChild(ul);
    return nav;
  }

  /* Keep the sticky rail clear of Ella's sticky header, which slides in and
     out as the reader scrolls. Measures the header's visible bottom edge and
     exposes it as --cb-toc-top on the layout. */
  function trackHeaderOffset(layout) {
    var GAP = 24;
    var ticking = false;

    function update() {
      ticking = false;
      var bottom = 0;
      var header = document.querySelector('.shopify-section-header-sticky');
      if (header && !header.classList.contains('shopify-section-header-hidden')) {
        bottom = Math.max(0, header.getBoundingClientRect().bottom);
      }
      layout.style.setProperty('--cb-toc-top', (bottom + GAP) + 'px');
    }

    function request() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    update();
  }

  /* Highlight the rail link for the section currently being read: the last
     heading whose top has scrolled above ~30% of the viewport. Sub-headings
     (e.g. FAQ questions) are only expanded under the active top-level
     section, so long articles don't overflow the rail. */
  function trackActiveHeading(headings, nav) {
    var links = {};
    var parentOf = {};
    nav.querySelectorAll('a[data-cb-toc-target]').forEach(function (a) {
      var id = a.getAttribute('data-cb-toc-target');
      links[id] = a;
      var parent = a.parentNode.getAttribute('data-cb-toc-parent');
      if (parent) parentOf[id] = parent;
    });
    var subItems = nav.querySelectorAll('[data-cb-toc-parent]');
    var rail = nav.parentNode;
    var current = null;
    var currentSection;

    function expand(section) {
      if (section === currentSection) return;
      currentSection = section;
      subItems.forEach(function (li) {
        li.classList.toggle('is-expanded', li.getAttribute('data-cb-toc-parent') === section);
      });
    }

    // Keep the active link inside the rail's visible area without scrolling the page.
    function reveal(link) {
      if (!rail || rail.scrollHeight <= rail.clientHeight) return;
      var top = link.getBoundingClientRect().top - rail.getBoundingClientRect().top + rail.scrollTop;
      if (top < rail.scrollTop) {
        rail.scrollTop = top - 8;
      } else if (top + link.offsetHeight > rail.scrollTop + rail.clientHeight) {
        rail.scrollTop = top + link.offsetHeight - rail.clientHeight + 8;
      }
    }
    var ticking = false;

    function update() {
      ticking = false;
      var line = window.innerHeight * 0.3;
      var active = null;
      for (var i = 0; i < headings.length; i++) {
        if (headings[i].getBoundingClientRect().top <= line) {
          active = headings[i].id;
        } else {
          break;
        }
      }
      if (active === current) return;
      if (current && links[current]) links[current].classList.remove('is-active');
      if (active && links[active]) links[active].classList.add('is-active');
      current = active;
      expand(active ? (parentOf[active] || active) : null);
      if (active && links[active]) reveal(links[active]);
    }

    function request() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    update();
  }

  function init() {
    var content = document.querySelector('.article-content.halo-text-format') ||
                  document.querySelector('.article-content');
    if (!content) return;

    if (content.querySelector('.cb-article-toc')) return; // already built

    var headings = content.querySelectorAll('h1, h2, h3, h4, h5, h6');
    var valid = [];
    var seen = {};

    headings.forEach(function (h) {
      var text = (h.textContent || '').trim();
      if (!text) return;                       // skip whitespace-only headings
      if (h.classList.contains('cb-no-toc')) return; // per-heading opt-out

      var base = slugify(text) || 'section';
      var id = base;
      if (seen[base]) {
        seen[base] += 1;
        id = base + '-' + seen[base];
      } else {
        seen[base] = 1;
      }
      if (!h.id) h.id = id;
      h.style.scrollMarginTop = '100px';       // keep anchor jumps clear of the sticky header
      valid.push(h);
    });

    if (valid.length < MIN_HEADINGS) return;

    var layout = content.closest('[data-cb-article-layout]');
    var rail = layout && layout.querySelector('[data-cb-toc-rail]');

    if (rail) {
      var railNav = buildNav(valid, 'rail');
      rail.appendChild(railNav);
      rail.hidden = false;
      layout.classList.add('cb-article-layout--has-toc');
      trackHeaderOffset(layout);
      trackActiveHeading(valid, railNav);
    }

    // Inline card: the only TOC when there's no rail; the tablet/mobile TOC otherwise.
    content.insertBefore(buildNav(valid, 'inline'), content.firstChild);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: rebuild when the article section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
